import _extends from "@babel/runtime-corejs3/helpers/extends";
import _slicedToArray from "@babel/runtime-corejs3/helpers/slicedToArray";
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import ReactModal from 'react-modal';
import { log } from '@jutro/logger';
import { useModalPromise } from './useModalPromise';
import { ModalNext } from './ModalNext';
import { AlertModal } from './AlertModal/AlertModal';
import { ConfirmationModal } from './ConfirmationModal/ConfirmationModal';
const createNoProviderWarning = methodName => () => log.error(`You should not use "${methodName}()" outside a <ModalNextProvider>`);
function getModalRoot() {
  const modalRoot = document.getElementById('modalRoot');
  if (modalRoot) {
    return modalRoot;
  }
  const deprecatedModalRoot = document.getElementById('confirmation');
  if (deprecatedModalRoot) {
    log.warning('ModalNextProvider: Found deprecated <div id="confirmation">. Please use <div id="modalRoot"> instead.');
    return deprecatedModalRoot;
  }
  log.warning('ModalNextProvider: Unable to find <div id="modalRoot">. Please add at the bottom of the <body> in your index.html.');
  return document.body;
}
export const ModalNextProvider = ({
  appElementId
}) => {
  const _useModalPromise = useModalPromise(),
    _useModalPromise2 = _slicedToArray(_useModalPromise, 4),
    isOpenAlert = _useModalPromise2[0],
    showAlertPromise = _useModalPromise2[1],
    resolveAlert = _useModalPromise2[2],
    rejectAlert = _useModalPromise2[3];
  const _useState = useState({}),
    _useState2 = _slicedToArray(_useState, 2),
    alertRender = _useState2[0].alertRender,
    setAlertState = _useState2[1];
  const _useModalPromise3 = useModalPromise(),
    _useModalPromise4 = _slicedToArray(_useModalPromise3, 4),
    isOpenModal = _useModalPromise4[0],
    showModalPromise = _useModalPromise4[1],
    resolveModal = _useModalPromise4[2],
    rejectModal = _useModalPromise4[3];
  const _useState3 = useState({}),
    _useState4 = _slicedToArray(_useState3, 2),
    modalRender = _useState4[0].modalRender,
    setModalState = _useState4[1];
  ModalNext.handleAfterClose = useCallback(() => {
    if (!isOpenAlert) {
      setAlertState({});
    }
    if (!isOpenModal) {
      setModalState({});
    }
  }, [isOpenAlert, isOpenModal, setAlertState, setModalState]);
  const context = useMemo(() => {
    const showAlert = ({
      status,
      icon,
      title,
      message,
      confirmButtonText
    }) => {
      setAlertState({
        alertRender: alertProps => React.createElement(AlertModal, _extends({}, alertProps, {
          parentSelector: getModalRoot,
          title: title,
          message: message,
          status: status,
          icon: icon,
          confirmButtonText: confirmButtonText
        }))
      });
      return showAlertPromise();
    };
    const showConfirm = ({
      status,
      icon,
      title,
      message,
      confirmButtonText,
      cancelButtonText
    }) => {
      setAlertState({
        alertRender: alertProps => React.createElement(ConfirmationModal, _extends({}, alertProps, {
          parentSelector: getModalRoot,
          title: title,
          message: message,
          status: status,
          icon: icon,
          confirmButtonText: confirmButtonText,
          cancelButtonText: cancelButtonText
        }))
      });
      return showAlertPromise();
    };
    const showModal = component => {
      setModalState({
        modalRender: modalProps => React.cloneElement(component, modalProps)
      });
      return showModalPromise();
    };
    return {
      showAlert,
      showConfirm,
      showModal
    };
  }, [showAlertPromise, setAlertState, showModalPromise, setModalState]);
  useEffect(() => {
    const elementExists = document.getElementById(appElementId);
    if (elementExists) {
      ReactModal.setAppElement(`#${appElementId}`);
    }
  }, []);
  ModalNextProvider.modalNextContext = context;
  return React.createElement(React.Fragment, null, alertRender && alertRender({
    isOpen: isOpenAlert,
    onResolve: resolveAlert,
    onReject: rejectAlert
  }), modalRender && modalRender({
    isOpen: isOpenModal,
    onResolve: resolveModal,
    onReject: rejectModal
  }));
};
ModalNextProvider.propTypes = {
  appElementId: PropTypes.string,
  children: PropTypes.node
};
ModalNextProvider.defaultProps = {
  appElementId: 'root'
};
ModalNextProvider.modalNextContext = {
  showAlert: createNoProviderWarning('showAlert'),
  showConfirm: createNoProviderWarning('showConfirm'),
  showModal: createNoProviderWarning('showModal')
};
ModalNextProvider.showAlert = props => {
  var _ModalNextProvider$mo;
  return (_ModalNextProvider$mo = ModalNextProvider.modalNextContext) === null || _ModalNextProvider$mo === void 0 ? void 0 : _ModalNextProvider$mo.showAlert(props);
};
ModalNextProvider.showConfirm = props => {
  var _ModalNextProvider$mo2;
  return (_ModalNextProvider$mo2 = ModalNextProvider.modalNextContext) === null || _ModalNextProvider$mo2 === void 0 ? void 0 : _ModalNextProvider$mo2.showConfirm(props);
};
ModalNextProvider.showModal = component => {
  var _ModalNextProvider$mo3;
  return (_ModalNextProvider$mo3 = ModalNextProvider.modalNextContext) === null || _ModalNextProvider$mo3 === void 0 ? void 0 : _ModalNextProvider$mo3.showModal(component);
};
ModalNextProvider.__docgenInfo = {
  componentName: "ModalNextProvider",
  packageName: "@jutro/components",
  description: "",
  displayName: "ModalNextProvider",
  methods: [],
  actualName: "ModalNextProvider",
  props: {
    appElementId: {
      type: {
        name: "string"
      },
      required: false,
      description: "ID of the root element of your app, used to hide the app from accessibility tools on modal popup.\nThe root element must not be a parent of the modal root (from getModalRoot()), or else the content of the modal will be hidden as well.\nDefaults to `\"root\"`",
      defaultValue: {
        value: "'root'",
        computed: false
      }
    },
    children: {
      type: {
        name: "node"
      },
      required: false,
      description: ""
    }
  }
};